/**
 * Design tokens — single source of truth for the visual language.
 *
 * Usage:
 *   import { COLORS, SHADOWS, RADIUS, FONT } from '../../styles/tokens'
 *
 * When adding new components, reference these constants instead of
 * hardcoding values. New tokens can be added here as the design evolves.
 */

// ── Colors ────────────────────────────────────────────────────────

export const COLORS = {
  // Brand greens
  greenDark:     '#1B4332',   // primary text, headings, dark buttons
  green:         '#2D6A4F',   // accent, links, active borders
  greenLight:    '#52B788',   // success, highlights
  greenPale:     '#B7E4C7',   // subtle borders, mint accents
  greenSurface:  '#F0FAF4',   // selected / active card background

  // Symmetry game accent colors (clearly distinguishable hues)
  symBlue:       '#2E4CB8',   // blue — medium/hard palette
  symPurple:     '#7B2D8E',   // purple — hard palette
  symOrange:     '#E8751A',   // orange — hard palette

  // Page & card backgrounds
  bgPage:   '#FAF6EF',   // warm off-white page background
  bgCard:   '#FFFFFF',   // card / panel background

  // Text
  textPrimary:   '#1B4332',   // headings, strong labels
  textBody:      '#6B4C3B',   // body copy, descriptions
  textSecondary: '#9A8070',   // secondary labels, hints
  textMuted:     '#C0AFA0',   // placeholder, very secondary
  textDark:      '#2E1F14',   // high-contrast text on light backgrounds

  // Borders & dividers
  borderWarm: '#EFE7D7',   // standard card / input border
  borderMint: '#B7E4C7',   // success-adjacent borders

  // Status — error
  errorText: '#C0392B',
  errorBg:   '#FFF0F0',

  // Status — warning (save error banner)
  warningText: '#92400E',
  warningBg:   '#FEF3C7',
  warningBorder: '#F59E0B',

  // Gradients
  gradientPrimary: 'linear-gradient(135deg, #1B4332, #2D6A4F)',

  // Chart/grid lines (radar chart, subtle dividers)
  gridLine:  '#E0D8D0',   // inner grid rings
  gridOuter: '#C8C0B8',   // outer grid ring and axis lines
} as const

// ── Shadows ───────────────────────────────────────────────────────

export const SHADOWS = {
  subtle: '0 2px 8px rgba(27,67,50,0.06)',     // inline elements, small cards
  card:   '0 2px 12px rgba(27,67,50,0.08)',     // standard card elevation
  medium: '0 4px 20px rgba(27,67,50,0.08)',     // raised panels
  active: '0 4px 16px rgba(45,106,79,0.15)',    // selected / active state
  strong: '0 6px 20px rgba(27,67,50,0.20)',     // dialogs, popovers
  modal:  '0 20px 64px rgba(27,67,50,0.16)',    // full modals
  button: '0 6px 24px rgba(27,67,50,0.25)',     // primary CTA buttons
} as const

// ── Border radii ──────────────────────────────────────────────────

export const RADIUS = {
  sm:   8,    // small buttons, chips, inputs
  md:   12,   // standard buttons, stat cards
  card: 14,   // option cards, difficulty selectors, mode selectors
  lg:   16,   // section cards, complete screens
  xl:   20,   // hero sections, large containers
  xxl:  24,   // full-width feature cards
  round: '50%' as const,   // avatars, circular badges
} as const

// ── Typography ────────────────────────────────────────────────────

export const FONT = {
  // Size scale
  size: {
    xs:   '0.75rem',   // ~12px — badges, tiny labels
    sm:   '0.85rem',   // ~14px — secondary text, captions
    base: '1rem',      // ~16px — body text (min for accessibility)
    md:   '1.1rem',    // ~18px — slightly emphasised body
    lg:   '1.2rem',    // ~19px — card titles, large buttons
    xl:   '1.5rem',    // ~24px — section headings
    h2:   '2rem',      // ~32px — page sub-headings
    h1:   '3rem',      // ~48px — page titles (Cormorant Garamond)
  },
  // Font families
  family: {
    body:    'inherit',
    display: "'Cormorant Garamond', serif",   // large headings
  },
} as const
